import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, ImagePlus, Loader2, MessageCircle, MessageSquareDashed, MoreHorizontal, RotateCw, Search, X, Image as ImageIcon, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, timeAgo } from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { useAuth } from "@/context/Auth";
import { Avatar, BackButton, PageHeader, Spinner } from "@/components/Common";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const hhmm = (s: string) => new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
function dayLabel(s: string) {
  const d = new Date(s), t = new Date();
  const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Oggi";
  if (d.toDateString() === y.toDateString()) return "Ieri";
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: d.getFullYear() === t.getFullYear() ? undefined : "numeric" });
}

/* ---------- inbox ---------- */

function Preview({ c, me }: any) {
  const lm = c.last_message;
  if (!lm) return null;
  const mine = lm.sender_id === me;
  return (
    <span className={`flex items-center gap-1 truncate text-sm ${c.unread ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
      {mine && <span className="shrink-0">Tu:</span>}
      {lm.has_photo && <ImageIcon size={13} className="shrink-0 text-[hsl(var(--cyan))]" />}
      <span className="truncate">{lm.text || (lm.has_photo ? "Foto" : "")}</span>
    </span>
  );
}

function ConversationRow({ c, me }: any) {
  const u = c.user || { display_name: "Account eliminato" };
  return (
    <Link to={`/messaggi/${c.id}`} data-testid="conversation-item" className={`flex items-center gap-3.5 px-4 py-3.5 rounded-2xl border ${c.unread ? "bg-[hsl(var(--surface-2))] border-[hsl(var(--primary)/.28)]" : "border-transparent hover:bg-[hsl(var(--surface))]"}`}>
      <Avatar user={u} size={52} ring={!!c.unread} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold truncate" data-testid="conversation-name">{u.display_name}</span>
          {u.username && <span className="text-xs text-muted-foreground truncate">@{u.username}</span>}
          <span className="ml-auto text-xs text-muted-foreground shrink-0" data-testid="conversation-time">{c.last_message_at && timeAgo(c.last_message_at)}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <div className="flex-1 min-w-0"><Preview c={c} me={me} /></div>
          {c.status === "pending" && c.is_requester && <span className="chip chip-violet shrink-0" data-testid="conversation-pending">In attesa</span>}
          {c.status === "rejected" && <span className="chip shrink-0">Non accettata</span>}
          {c.unread > 0 && <span className="badge-lime shrink-0" data-testid="conversation-unread">{c.unread > 99 ? "99+" : c.unread}</span>}
        </div>
      </div>
    </Link>
  );
}

function InboxEmpty({ requests }: { requests: boolean }) {
  return (
    <div className="surface p-10 flex flex-col items-start gap-4 max-w-lg" data-testid={requests ? "requests-empty" : "inbox-empty"}>
      <span className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[hsl(var(--primary)/.12)] border border-[hsl(var(--primary)/.35)] text-[hsl(var(--primary))] glow-violet"><MessageSquareDashed size={26} /></span>
      <div>
        <h2 className="font-display text-xl font-semibold">{requests ? "Nessuna richiesta" : "Ancora nessuna conversazione"}</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-[42ch]">{requests ? "Quando qualcuno che non è tra le tue connessioni ti scrive, il messaggio arriva qui e decidi tu se accettarlo." : "Apri il profilo di un creator e tocca Messaggio per iniziare a parlare in privato."}</p>
      </div>
      {!requests && <Link to="/cerca" className="btn btn-primary text-sm" data-testid="inbox-find-people-btn"><Search size={16} />Trova creator</Link>}
    </div>
  );
}

export function Messaggi() {
  const { user } = useAuth();
  const [box, setBox] = useState<"inbox" | "requests">("inbox");
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["dm-list", box], queryFn: () => api.get("/dm/conversations", { params: { box } }).then((r) => r.data), refetchInterval: 8000 });
  const { data: unread } = useQuery({ queryKey: ["dm-unread"], queryFn: () => api.get("/dm/unread").then((r) => r.data), refetchInterval: 15000 });
  return (
    <div data-testid="messaggi-page">
      <PageHeader kicker="Privati, solo tra voi due" title="Messaggi" />
      <div className="px-5 md:px-10 max-w-3xl space-y-5 pb-10">
        <div className="seg w-fit" role="tablist">
          {([["inbox", "Chat"], ["requests", "Richieste"]] as const).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={box === k} onClick={() => setBox(k)} data-testid={`dm-tab-${k}`} className={`seg-item ${box === k ? "seg-active" : ""}`}>
              {l}{k === "requests" && unread?.requests > 0 && <span className="badge-lime ml-1.5" data-testid="dm-requests-count">{unread.requests}</span>}
            </button>
          ))}
        </div>
        {isLoading ? <Spinner /> : isError ? (
          <div className="surface p-5 flex items-center justify-between gap-3" data-testid="inbox-error"><span className="text-sm">Impossibile caricare i messaggi.</span><button className="btn btn-ghost text-sm" onClick={() => refetch()}><RotateCw size={14} />Riprova</button></div>
        ) : !data.length ? <InboxEmpty requests={box === "requests"} /> : (
          <div className="space-y-1" data-testid="conversation-list">{data.map((c: any) => <ConversationRow key={c.id} c={c} me={user.id} />)}</div>
        )}
      </div>
    </div>
  );
}

/* ---------- chat ---------- */

function useThread(id: string, me: string) {
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState<any[]>([]);
  const [otherRead, setOtherRead] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [more, setMore] = useState(false);
  const lastRef = useRef<string | undefined>();
  const markRead = useCallback(() => api.post(`/dm/conversations/${id}/read`).then(() => { qc.invalidateQueries({ queryKey: ["dm-unread"] }); qc.invalidateQueries({ queryKey: ["unread"] }); }).catch(() => {}), [id, qc]);
  const merge = useCallback((rows: any[]) => setMsgs((xs) => {
    const seen = new Set(xs.map((x) => x.id));
    const add = rows.filter((r) => !seen.has(r.id));
    return add.length ? [...xs, ...add] : xs;
  }), []);
  const idsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    lastRef.current = msgs.filter((m) => !m.local).slice(-1)[0]?.created_at;
    idsRef.current = new Set(msgs.map((m) => m.id));
  }, [msgs]);
  const load = useCallback(() => {
    setState("loading");
    api.get(`/dm/conversations/${id}/messages`).then(({ data }) => {
      setMsgs(data.messages); setOtherRead(data.other_last_read); setMore(data.messages.length === 50); setState("ready"); markRead();
    }).catch(() => setState("error"));
  }, [id, markRead]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (state !== "ready") return;
    const iv = setInterval(async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get(`/dm/conversations/${id}/messages`, { params: lastRef.current ? { after: lastRef.current } : {} });
        setOtherRead(data.other_last_read);
        const fresh = data.messages.filter((m: any) => !idsRef.current.has(m.id));
        if (fresh.length) { merge(fresh); if (fresh.some((m: any) => m.sender_id !== me)) markRead(); }
      } catch { /* next tick retries */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [state, id, me, merge, markRead]);
  const older = async () => {
    const first = msgs.find((m) => !m.local);
    if (!first) return;
    const { data } = await api.get(`/dm/conversations/${id}/messages`, { params: { before: first.created_at } });
    setMsgs((xs) => [...data.messages, ...xs]); setMore(data.messages.length === 50);
  };
  return { msgs, setMsgs, merge, otherRead, state, more, older, load };
}

function Bubble({ m, mine, read, onRetry }: any) {
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={mine ? "message-mine" : "message-theirs"}>
      <div className={`max-w-[78%] sm:max-w-[60%] ${mine ? "bubble-mine" : "bubble-theirs"} ${m.failed ? "opacity-60" : ""}`}>
        {m.media && <a href={mediaUrl(m.media.url)} target="_blank" rel="noreferrer"><img src={mediaUrl(m.media.url)} alt="Foto" className="rounded-xl max-h-80 w-auto mb-1" data-testid="message-photo" /></a>}
        {m.localPreview && <img src={m.localPreview} alt="Foto" className="rounded-xl max-h-80 w-auto mb-1" />}
        {m.text && <p className="whitespace-pre-wrap break-words text-[15px] leading-snug" data-testid="message-text">{m.text}</p>}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1 px-1 flex items-center gap-1.5" data-testid="message-meta">
        {m.failed ? <><span className="text-[hsl(var(--magenta))]">Non inviato</span><button onClick={onRetry} className="underline" data-testid="message-retry-btn">Riprova</button></>
          : m.local ? <><Loader2 size={10} className="animate-spin" />Invio…</> : <>{hhmm(m.created_at)}{mine && read && <span className="text-[hsl(var(--cyan))]" data-testid="message-read">· Letto</span>}</>}
      </div>
    </div>
  );
}

function ThreadBanner({ conv, onAccept, onDecline, onBlock }: any) {
  if (conv.blocked) return <div className="banner" data-testid="chat-blocked-banner"><Ban size={16} />Non potete scrivervi: c'è un blocco attivo tra voi.</div>;
  if (conv.status === "rejected") return <div className="banner" data-testid="chat-rejected-banner">{conv.is_requester ? "La tua richiesta di messaggio non è stata accettata." : "Hai rifiutato questa richiesta."}</div>;
  if (conv.status !== "pending") return null;
  if (conv.is_requester) return <div className="banner" data-testid="chat-pending-banner">Richiesta inviata. Puoi mandare fino a 3 messaggi finché @{conv.user?.username} non accetta.</div>;
  return (
    <div className="banner flex-col items-start gap-3" data-testid="chat-request-banner">
      <span><b>@{conv.user?.username}</b> vuole scriverti. Non saprà se hai letto finché non accetti.</span>
      <div className="flex gap-2">
        <button className="btn btn-primary text-sm py-2" onClick={onAccept} data-testid="dm-accept-btn">Accetta</button>
        <button className="btn btn-ghost text-sm py-2" onClick={onDecline} data-testid="dm-decline-btn">Rifiuta</button>
        <button className="btn btn-ghost text-sm py-2" onClick={onBlock} data-testid="dm-block-from-request-btn">Blocca</button>
      </div>
    </div>
  );
}

function Composer({ convId, canSend, onSent, onLocal, onFail }: any) {
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState<{ file: File; preview: string; progress: number; media?: any; error?: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const pick = async (f: File) => {
    const ctrl = new AbortController();
    const preview = URL.createObjectURL(f);
    setPhoto({ file: f, preview, progress: 0 });
    try {
      const media = await chunkedUpload(f, ctrl.signal, (p) => setPhoto((x) => x && { ...x, progress: p }), undefined, "dm");
      setPhoto((x) => x && { ...x, media, progress: 100 });
    } catch (e) { setPhoto((x) => x && { ...x, error: errMsg(e) }); }
  };
  const send = () => {
    const t = text.trim();
    if ((!t && !photo?.media) || (photo && !photo.media)) return;
    const local = { id: `local-${Date.now()}`, local: true, text: t, created_at: new Date().toISOString(), localPreview: photo?.preview, body: { text: t, media_id: photo?.media?.id } };
    setText(""); setPhoto(null);
    onLocal(local);
    api.post(`/dm/conversations/${convId}/messages`, local.body).then(({ data }) => onSent(local.id, data)).catch((e) => { onFail(local.id); toast.error(errMsg(e)); });
  };
  if (!canSend) return null;
  const ready = (text.trim() || photo?.media) && !(photo && !photo.media);
  return (
    <div className="composer" data-testid="chat-composer">
      {photo && (
        <div className="relative w-20 h-20 mb-2 rounded-xl overflow-hidden border border-ink/10" data-testid="composer-photo">
          <img src={photo.preview} alt="" className="w-full h-full object-cover" />
          {!photo.media && !photo.error && <div className="absolute inset-0 bg-black/55 text-white flex items-center justify-center text-[11px] font-mono" data-testid="composer-photo-progress">{photo.progress}%</div>}
          {photo.error && <button onClick={() => pick(photo.file)} className="absolute inset-0 bg-black/70 text-white flex flex-col items-center justify-center text-[10px] text-[hsl(var(--magenta))]" data-testid="composer-photo-retry"><RotateCw size={14} />Riprova</button>}
          <button onClick={() => setPhoto(null)} aria-label="Rimuovi foto" className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5" data-testid="composer-photo-remove"><X size={12} /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" data-testid="composer-file-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ""; }} />
        <button onClick={() => fileRef.current?.click()} aria-label="Allega foto" className="icon-btn" data-testid="composer-attach-btn"><ImagePlus size={20} /></button>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={1} placeholder="Scrivi un messaggio…" data-testid="composer-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-transparent outline-none py-2.5 max-h-32 text-[15px] placeholder:text-muted-foreground" />
        <button onClick={send} disabled={!ready} aria-label="Invia" className="send-btn" data-testid="composer-send-btn"><ArrowUp size={20} strokeWidth={2.4} /></button>
      </div>
    </div>
  );
}

function ChatMenu({ conv, onBlockToggle }: any) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button aria-label="Opzioni" className="icon-btn" data-testid="chat-menu-btn"><MoreHorizontal size={20} /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="menu-panel">
        {conv.user?.username && <DropdownMenuItem asChild><Link to={`/u/${conv.user.username}`} data-testid="chat-menu-profile">Vedi profilo</Link></DropdownMenuItem>}
        <DropdownMenuItem onClick={onBlockToggle} className="text-[hsl(var(--magenta))]" data-testid="chat-menu-block">{conv.blocked_by_me ? "Sblocca utente" : "Blocca utente"}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Sticks to the bottom only for new messages at the end (not when older ones are prepended),
// keeps the reading position when history loads, and doesn't yank the view if the user scrolled up.
function useChatScroll(msgs: any[], me: string, ready: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const prev = useRef({ first: "", last: "", height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !msgs.length) return;
    const first = msgs[0].id, lastMsg = msgs[msgs.length - 1], p = prev.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (p.first && first !== p.first && lastMsg.id === p.last) el.scrollTop += el.scrollHeight - p.height;
    else if (lastMsg.id !== p.last && (!p.last || nearBottom || lastMsg.local || lastMsg.sender_id === me)) el.scrollTop = el.scrollHeight;
    prev.current = { first, last: lastMsg.id, height: el.scrollHeight };
  }, [msgs, me, ready]); // `ready`: the list container mounts only once the conversation header has loaded
  return ref;
}

// iOS keyboard: the layout viewport doesn't shrink, the visual one does. Pin the chat to the visual viewport
// so the composer stays right above the keyboard.
function useVisualViewport() {
  const read = () => ({ top: window.visualViewport?.offsetTop ?? 0, height: window.visualViewport?.height ?? window.innerHeight });
  const [vv, setVv] = useState(read);
  useEffect(() => {
    const v = window.visualViewport;
    const on = () => setVv(read());
    v?.addEventListener("resize", on); v?.addEventListener("scroll", on); window.addEventListener("resize", on);
    return () => { v?.removeEventListener("resize", on); v?.removeEventListener("scroll", on); window.removeEventListener("resize", on); };
  }, []);
  return vv;
}

export function Chat() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: conv, refetch, isError } = useQuery({ queryKey: ["dm-conv", id], queryFn: () => api.get(`/dm/conversations/${id}`).then((r) => r.data), refetchInterval: 10000 });
  const { data: blocks, refetch: refetchBlocks } = useQuery({ queryKey: ["blocks"], queryFn: () => api.get("/me/blocks").then((r) => r.data) });
  const th = useThread(id, user.id);
  const box = useChatScroll(th.msgs, user.id, !!conv);
  const vv = useVisualViewport();
  if (isError) return <div className="p-10 space-y-4" data-testid="chat-not-found"><p>Conversazione non trovata.</p><Link to="/messaggi" className="btn btn-ghost">Torna ai messaggi</Link></div>;
  if (!conv) return <div className="p-10"><Spinner /></div>;
  const blockedByMe = !!blocks?.some((b: any) => b.id === conv.user?.id);
  const c = { ...conv, blocked_by_me: blockedByMe };
  const act = async (fn: () => Promise<any>, ok?: string) => { try { await fn(); if (ok) toast.success(ok); refetch(); refetchBlocks(); qc.invalidateQueries({ queryKey: ["dm-list"] }); qc.invalidateQueries({ queryKey: ["dm-unread"] }); } catch (e) { toast.error(errMsg(e)); } };
  const toggleBlock = () => act(() => blockedByMe ? api.delete(`/users/${conv.user.id}/block`) : api.post(`/users/${conv.user.id}/block`), blockedByMe ? "Utente sbloccato" : "Utente bloccato");
  const lastMine = [...th.msgs].reverse().find((m) => m.sender_id === user.id && !m.local);
  let prevDay = "";
  return (
    <div className="fixed inset-x-0 md:left-[16.5rem] flex flex-col" style={{ top: vv.top, height: vv.height }} data-testid="chat-page">
      <header className="chat-header">
        <BackButton parent="/messaggi" />
        <Link to={conv.user?.username ? `/u/${conv.user.username}` : "#"} className="flex items-center gap-3 min-w-0 flex-1" data-testid="chat-header-user">
          <Avatar user={conv.user || {}} size={40} />
          <div className="min-w-0"><div className="font-semibold truncate">{conv.user?.display_name || "Account eliminato"}</div>{conv.user?.username && <div className="text-xs text-muted-foreground truncate">@{conv.user.username}</div>}</div>
        </Link>
        {conv.user && <ChatMenu conv={c} onBlockToggle={toggleBlock} />}
      </header>
      <div ref={box} className="flex-1 overflow-y-auto overscroll-contain px-4 md:px-8 py-5 space-y-3" data-testid="chat-messages">
        {th.more && <button onClick={th.older} className="btn btn-ghost text-xs mx-auto flex" data-testid="chat-load-older">Carica messaggi precedenti</button>}
        {th.state === "loading" && <Spinner />}
        {th.state === "error" && <div className="banner" data-testid="chat-error">Impossibile caricare la chat. <button className="underline" onClick={th.load}>Riprova</button></div>}
        {th.state === "ready" && !th.msgs.length && <p className="text-center text-sm text-muted-foreground py-10" data-testid="chat-empty">Nessun messaggio. Rompi il ghiaccio con un saluto.</p>}
        {th.msgs.map((m) => {
          const day = dayLabel(m.created_at);
          const sep = day !== prevDay; prevDay = day;
          return (
            <React.Fragment key={m.id}>
              {sep && <div className="text-center"><span className="day-sep" data-testid="chat-day-separator">{day}</span></div>}
              <Bubble m={m} mine={m.local || m.sender_id === user.id} read={m.id === lastMine?.id && th.otherRead && th.otherRead >= m.created_at}
                onRetry={() => { th.setMsgs((xs) => xs.map((x) => (x.id === m.id ? { ...x, failed: false } : x))); api.post(`/dm/conversations/${id}/messages`, m.body).then(({ data }) => th.setMsgs((xs) => [...xs.filter((x) => x.id !== m.id && x.id !== data.id), data])).catch((e) => { toast.error(errMsg(e)); th.setMsgs((xs) => xs.map((x) => (x.id === m.id ? { ...x, failed: true } : x))); }); }} />
            </React.Fragment>
          );
        })}
      </div>
      <div className="px-3 md:px-8 pb-[max(env(safe-area-inset-bottom),12px)] pt-2 space-y-2">
        <ThreadBanner conv={conv} onAccept={() => act(() => api.post(`/dm/conversations/${id}/accept`), "Richiesta accettata")}
          onDecline={() => act(async () => { await api.post(`/dm/conversations/${id}/decline`); nav("/messaggi"); }, "Richiesta rifiutata")} onBlock={toggleBlock} />
        <Composer convId={id} canSend={conv.can_send && !(conv.status === "pending" && !conv.is_requester)}
          onLocal={(m: any) => th.setMsgs((xs) => [...xs, m])}
          onSent={(lid: string, real: any) => { th.setMsgs((xs) => [...xs.filter((x) => x.id !== lid && x.id !== real.id), real]); refetch(); }}
          onFail={(lid: string) => th.setMsgs((xs) => xs.map((x) => (x.id === lid ? { ...x, failed: true } : x)))} />
      </div>
    </div>
  );
}

export function MessageButton({ userId }: { userId: string }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/dm/conversations", { user_id: userId }); nav(`/messaggi/${data.id}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <button onClick={go} disabled={busy} className="btn btn-ghost" data-testid="profile-message-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={16} />}Messaggio</button>;
}
